<script setup lang="ts">
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "reka-ui";
import { useRouter } from "vue-router";
import { computed, ref, watch } from "vue";

import AlertDialog from "@/components/AlertDialog.vue";
import Dockview from "@/components/Dockview/Dockview.vue";

import { $can } from "@/services/UtilitiesService";

import { useBrandStore, useMessageStore, useUserSessionStore } from "@/stores";

import Timeline from "@/components/Timeline.vue";
import CaseMenuBar from "@/components/CaseMenuBar.vue";

import { useAllCasePanels, type PanelType } from "@/hooks/use-case-panel";

defineProps<{ isCaseRoute: boolean }>();

const $router = useRouter();

const allPanels = useAllCasePanels();

const wrapperRef = ref<HTMLDivElement | null>(null);
const dockviewRef = ref<InstanceType<typeof Dockview>>();
const configStyleSheet = ref<CSSStyleSheet>();

const closedPanels = computed(() => {
  const selectedLens = useBrandStore().selectedLens.code;
  const closedPanels = Object.keys(
    useUserSessionStore().brandLayout?.closedPanelLastGroup?.[selectedLens] ?? {},
  ) as PanelType[];

  return closedPanels
    .map((id) => [id, allPanels.value[id].title] as const)
    .sort(([, titleA], [, titleB]) => titleA.localeCompare(titleB));
});

const hasTimeline = computed(() => {
  return !!useBrandStore().viewerConfig.timeline?.general?.useLower;
});

const config = computed(() => {
  return useBrandStore().viewerConfig;
});

const hasLensDefaultLayout = computed(() => useBrandStore().viewerConfig.flexDefaultLayout != null);

const hasLensCustomizedLayout = computed(() => {
  const selectedLens = useBrandStore().selectedLens.code;
  return useUserSessionStore().brandLayout?.lenses[selectedLens] != null;
});

watch(
  config,
  (to) => {
    if (!to) return;
    addStyleBlock();
  },
  { immediate: true },
);

async function resetLensCustomLayout() {
  try {
    await useUserSessionStore().resetBrandLensLayout();
  } finally {
    $router.go(0);
  }
}

async function updateLensDefaultLayout() {
  const selectedLens = useBrandStore().selectedLens.code;
  const { brandLayout: layout, brandSort: sort } = useUserSessionStore();
  const currentLensLayout = layout?.lenses?.[selectedLens];

  if (layout == null || currentLensLayout == null) return;

  try {
    await Promise.all([
      useUserSessionStore().saveBrandSession({
        sort,
        layout: {
          provider: layout.provider,
          lenses: { ...layout?.lenses, [selectedLens]: undefined },
          closedPanelLastGroup: { ...layout.closedPanelLastGroup, [selectedLens]: undefined },
        },
      }),
      useBrandStore().saveLensDefaultLayout({ provider: layout.provider, state: currentLensLayout }),
    ]);

    useMessageStore().updateMessage({
      type: "info",
      autoHide: true,
      message: "Your current layout is now the default for all users on this lens.",
      title: "Default Layout Updated",
      showReload: false,
    });
  } catch (e) {
    console.error(e);

    useMessageStore().updateMessage({
      type: "error",
      autoHide: false,
      message: "Failed to update the default layout. Please try again.",
      title: "Update Failed",
      showReload: false,
    });
  }
}

async function resetLensDefaultLayout() {
  try {
    await useBrandStore().saveLensDefaultLayout(undefined);
    $router.go(0);
  } catch (e) {
    console.error(e);
    useMessageStore().updateMessage({
      type: "error",
      autoHide: false,
      message: "Failed to reset the default layout. Please try again.",
      title: "Reset Failed",
      showReload: false,
    });
  }
}

function addStyleBlock(): void {
  const timelineConfig = config.value.timeline;
  const recordListConfig = config.value.recordList;

  if (!recordListConfig && !timelineConfig) return;

  if (configStyleSheet.value == null) {
    configStyleSheet.value = new CSSStyleSheet();
    document.adoptedStyleSheets.push(configStyleSheet.value);
  }

  const styles: string[] = [];

  if (recordListConfig) {
    const { autoTags = [], evidenceCounts = [], userTags = [] } = recordListConfig;

    // Output user tag styling
    userTags.forEach((tag) => {
      if (tag.display === "none") return [];
      const name = `.${tag.category.replace(/\s/g, "").toLowerCase()}`;
      if (tag.color != null) styles.push(`${name} { color: ${tag.color} !important; }`);
      if (tag.background != null) styles.push(`${name} { background: ${tag.background} !important; }`);
    });

    // Output auto tag styling
    autoTags.forEach((tag) => {
      if (tag.display === "none") return [];
      tag.enumeration.forEach(({ color, background, value }) => {
        const name = `.${tag.category.replace(/\s/g, "").toLowerCase()}-${value.replace(/\s/g, "").toLowerCase()}`;

        if (color != null) styles.push(`${name} { color: ${color} !important; }`);
        if (background != null) styles.push(`${name} { background: ${background} !important; }`);
      });
    });

    // Output evidence count styling
    evidenceCounts.forEach((tag) => {
      if (tag.display === "none") return [];
      const name = `.${tag.name.replace(/\s/g, "").toLowerCase()}`;
      if (tag.color != null) styles.push(`${name} { color: ${tag.color} !important; }`);
      if (tag.background != null) styles.push(`${name} { background: ${tag.background} !important; }`);
    });
  }

  if (timelineConfig?.instances) {
    // Output instance styling
    const { defaultBackground, defaultColour, values } = timelineConfig.instances.instanceStyle;
    styles.push(`.instance { color: ${defaultColour}; background: ${defaultBackground}; }`);

    const tag = timelineConfig.instances.instanceStyle.instanceTag.replace(/\s/g, "").toLowerCase();
    values?.forEach(({ color, background, value }) => {
      styles.push(
        `.${tag}-${value.replace(/\s/g, "").toLowerCase()} { color: ${color} !important; background: ${background} !important; }`,
      );
    });
  }

  configStyleSheet.value.replaceSync(styles.join("\n"));
}

function resetScrollTop() {
  if (wrapperRef.value == null) return;
  wrapperRef.value.scrollTop = 0;
}
</script>
<template>
  <CaseMenuBar>
    <DropdownMenuRoot v-if="hasLensCustomizedLayout || hasLensDefaultLayout || !!closedPanels?.length">
      <DropdownMenuTrigger class="tw-btn-md-green invert tw-min-w-20">
        Layout
        <i aria-hidden="true" class="fas fa-fw fa-chevron-down" />
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="end"
          class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
          :side-offset="4"
        >
          <DropdownMenuItem
            v-if="hasLensCustomizedLayout"
            class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
            @select="resetLensCustomLayout"
          >
            {{ hasLensDefaultLayout ? "Reset to Lens Default" : "Reset" }}
          </DropdownMenuItem>
          <DropdownMenuSub v-if="$can('manageClientConfig') && (hasLensCustomizedLayout || hasLensDefaultLayout)">
            <DropdownMenuSubTrigger
              class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400 data-[state=open]:tw-bg-gray-400"
            >
              Manage Lens Default Layout
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                align="start"
                :side-offset="15"
                class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
              >
                <DropdownMenuArrow class="tw-fill-gray-500" />
                <AlertDialog
                  v-if="hasLensCustomizedLayout"
                  title="Update Default Layout"
                  description="Your current case layout will become the default for all users on this lens."
                  action-label="Update"
                  @confirmed="updateLensDefaultLayout"
                >
                  <DropdownMenuItem
                    class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                    @select.prevent
                  >
                    Update
                  </DropdownMenuItem>
                </AlertDialog>
                <AlertDialog
                  v-if="hasLensDefaultLayout"
                  title="Reset Default Layout"
                  description="This will remove the default case layout for all users on this lens. Each user will see the system default instead."
                  action-label="Reset"
                  @confirmed="resetLensDefaultLayout"
                >
                  <DropdownMenuItem
                    class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                    @select.prevent
                  >
                    Reset
                  </DropdownMenuItem>
                </AlertDialog>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuSub v-if="closedPanels?.length">
            <DropdownMenuSubTrigger
              class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400 data-[state=open]:tw-bg-gray-400"
            >
              Closed Panels
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                align="start"
                :side-offset="15"
                class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
              >
                <DropdownMenuArrow class="tw-fill-gray-500" />
                <DropdownMenuItem
                  v-for="[id, title] in closedPanels"
                  :key="id"
                  class="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                  @select.prevent="() => dockviewRef?.openPanel(id)"
                >
                  {{ title }}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>
  </CaseMenuBar>
  <router-view v-if="!isCaseRoute" />
  <div v-else class="tw-flex tw-h-full tw-flex-col tw-overflow-y-hidden">
    <Timeline v-if="hasTimeline" />
    <div ref="wrapperRef" class="tw-relative tw-flex-1 tw-overflow-hidden" @scroll="resetScrollTop">
      <Dockview ref="dockviewRef" :all-panels="allPanels" />
    </div>
  </div>
</template>
